"use client";

/**
 * Public "Listen on" (streaming) + "Follow" (social / web) groups for a
 * person's or artist's linked accounts. Shared by /profile/[id] and the
 * artist page. Every link opens in a new tab; every link can be shared
 * (native share sheet, clipboard on desktop) with the link's own URL.
 */

import { useId } from "react";
import { useTranslations } from "next-intl";
import { Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { shareContent } from "@/lib/share";
import { cn } from "@/lib/utils";
import { displayHost, type LinkedAccount } from "@/lib/social-platforms";
import { PlatformLogo } from "@/components/social/platform-logo";

type Group = "listen" | "follow";

export function useAccountLabel() {
  const t = useTranslations("profile.socialHub");
  return (a: LinkedAccount) =>
    a.id === "website" ? (a.customLabel || displayHost(a.url) || t("website")) : a.label;
}

export function LinkedAccounts({
  links,
  name,
  groups = ["listen", "follow"],
  className,
}: {
  links: LinkedAccount[];
  /** Whose links these are — used in the share text. */
  name: string;
  groups?: Group[];
  className?: string;
}) {
  const t = useTranslations("profile.socialHub.public");
  const labelOf = useAccountLabel();
  const { toast } = useToast();
  const baseId = useId();

  const streaming = links.filter((l) => l.kind === "streaming");
  const other = links.filter((l) => l.kind !== "streaming");
  const showListen = groups.includes("listen") && streaming.length > 0;
  const showFollow = groups.includes("follow") && other.length > 0;
  if (!showListen && !showFollow) return null;

  const share = async (a: LinkedAccount) => {
    const platform = labelOf(a);
    const native = typeof navigator !== "undefined" && typeof navigator.share === "function";
    const ok = await shareContent({ title: t("shareTitle", { name, platform }), url: a.url, external: true });
    // The native share sheet is its own feedback; the clipboard fallback isn't.
    if (ok && !native) toast({ title: t("linkCopied"), description: t("linkCopiedDesc") });
  };

  const renderGroup = (group: Group, items: LinkedAccount[]) => (
    <section aria-labelledby={`${baseId}-${group}`} className="space-y-1.5">
      <h3 id={`${baseId}-${group}`} className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
        {group === "listen" ? t("listenOn") : t("follow")}
      </h3>
      <ul className="flex flex-wrap gap-2">
        {items.map((a) => {
          const platform = labelOf(a);
          return (
            <li key={`${a.id}-${a.url}`} className="flex max-w-full min-w-0 items-stretch gap-1">
              <Button asChild variant="outlineContrast" className="h-11 min-w-0 max-w-full gap-2 px-3 [&_svg]:size-5">
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={group === "listen" ? t("listenOnAria", { platform }) : t("followAria", { platform })}
                >
                  <PlatformLogo id={a.id} url={a.url} />
                  <span className="truncate max-w-[12rem]">{platform}</span>
                </a>
              </Button>
              <Button
                type="button"
                variant="outlineContrast"
                size="icon"
                className="h-11 w-11 flex-shrink-0"
                onClick={() => share(a)}
                aria-label={t("shareAria", { platform })}
                title={t("shareAria", { platform })}
              >
                <Share2 aria-hidden="true" />
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );

  return (
    <div className={cn("space-y-3 min-w-0", className)}>
      {showListen && renderGroup("listen", streaming)}
      {showFollow && renderGroup("follow", other)}
    </div>
  );
}
